import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useFormik } from "formik";
import moment from "moment";

import { toast } from "react-toastify";
import { isEmpty } from "lodash";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  IconArrowDown,
  IconCalendar,
  IconMedal,
  IconPlus,
  IconTrash,
  IconUsers,
} from "@tabler/icons-react";

import { useMutation, useQuery } from "@tanstack/react-query";

import { Input } from "../components/Input";
import ContainerLoading from "../components/ContainerLoading";
import PageHeader from "../components/PageHeader";
import { Empty } from "../components/Empty";

import { classById, classOptions, classUpdate } from "../services/class";
import { useMediaQuery, useTheme } from "@mui/material";

const daysOfWeek = [
  { id: "1", name: "Dom" },
  { id: "2", name: "Seg" },
  { id: "3", name: "Ter" },
  { id: "4", name: "Qua" },
  { id: "5", name: "Qui" },
  { id: "6", name: "Sex" },
  { id: "7", name: "Sab" },
];

const ClassDetails = () => {
  const [fightId, setFightId] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [professorId, setProfessorId] = useState<string[]>([]);
  const [studentsIds, setStudentsIds] = useState<string[]>([]);
  const [openStudents, setOpenStudents] = useState(false);

  const [searchStudent, setSearchStudent] = useState("");

  const [dayWeek, setDayWeek] = useState<string[]>([]);

  const [studentLimit, setStudentLimit] = useState(false);
  const [checkinLock, setCheckinLock] = useState(false);
  const { id }: any = useParams();

  const [expanded, setExpanded] = useState<string | false>("panel1");

  const handleChangeAccordion =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleDayWeek = (
    _event: React.MouseEvent<HTMLElement>,
    paymentsType: string[]
  ) => {
    setDayWeek(paymentsType);
  };

  const onCloseStudent = () => {
    setOpenStudents(false);
    setSearchStudent("");
  };

  const { data, isLoading, isFetching } = useQuery({
    queryFn: () => classById(id),
    queryKey: ["classById" + id],
    initialData: {},
  });

  const {
    data: dataOptions,
    isLoading: isLoadingOptions,
    isFetching: isFetchingOptions,
  }: {
    data: {
      professor: { id: string; fullName: string }[];
      students: { id: string; fullName: string }[];
      spaces: [{ id: string; name: string }];
      fights: [{ id: string; name: string }];
    };
    isLoading: boolean;
    isFetching: boolean;
  } = useQuery({
    queryFn: classOptions,
    queryKey: ["classOptions"],
    initialData: {
      professor: [],
      spaces: [],
      fights: [],
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: classUpdate,
    onSuccess: () => {
      toast.success("Turma atualizada");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: {
      id: "",
      name: "",
      description: "",
      fightsId: "",
      spacesId: "",
      professorId: "",
      limit: "",
      dayWeek: [],
      automaticCompletion: Boolean(),
      startTime: moment().format("HH:mm").toString(),
      endTime: moment().add(1, "hour").format("HH:mm").toString(),
      recurrent: "1",
    },
    onSubmit: (values) => {
      mutate({
        ...values,
        startTime: moment(values.startTime, "HH:mm").toDate(),
        endTime: moment(values.endTime, "HH:mm").toDate(),
        fightsId: fightId,
        spacesId: spaceId,
        professorId,
        students: studentsIds,
        dayWeek: dayWeek.join(","),
        // automaticCompletion: checked,
        checkinLock,
      });
    },
  });

  useEffect(() => {
    if (data) {
      setFieldValue("id", data?.id);
      setFieldValue(
        "startTime",
        moment(data?.startTime).format("HH:mm").toString()
      );
      setFieldValue(
        "endTime",
        moment(data?.endTime).format("HH:mm").toString()
      );
      setFieldValue("name", data?.name);
      setFieldValue("description", data?.description);
      setFieldValue("limit", data?.limit);
      setDayWeek(data?.dayWeek?.split(","));
      setFightId(data?.fightsId);
      setSpaceId(data?.spacesId);
      // setChecked(data?.automaticCompletion);
      setProfessorId(
        data.professors?.map((item: any) => item?.professorId) ?? []
      );
      setStudentsIds(data.students?.map((item: any) => item?.studentId) ?? []);
      setCheckinLock(data?.checkinLock);
      if (Number(data?.limit) > 0) {
        setStudentLimit(true);
      }
    }
  }, [data]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <ContainerLoading
      loading={
        isPending ||
        isLoadingOptions ||
        isFetchingOptions ||
        isLoading ||
        isFetching
      }
    >
      <PageHeader
        title=" Detalhes da turma"
        subtitle="  Você pode visualizar e editar os dados da turma aqui."
        back
        padding={2}
      />
      <Grid2 container spacing={2} padding={2}>
        <Grid2 size={12}>
          <Accordion
            defaultExpanded
            expanded={expanded === "panel1"}
            onChange={handleChangeAccordion("panel1")}
          >
            <AccordionSummary
              expandIcon={<IconArrowDown />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
                <IconMedal size={20} />
                <Typography>Dados da aula</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid2 size={12}>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Input
                      id="name"
                      name="name"
                      label="Nome"
                      placeholder="Nome da turma"
                      type="text"
                      fullWidth
                      size="small"
                      value={values.name}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Input
                      id="description"
                      name="description"
                      label="Descrição"
                      placeholder="Descrição"
                      type="text"
                      fullWidth
                      size="small"
                      value={values.description}
                      onChange={handleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      Modalidades
                    </Typography>
                    <Select
                      sx={{ marginTop: 1 }}
                      id="fightId"
                      value={fightId}
                      fullWidth
                      onChange={handleChange}
                    >
                      {dataOptions?.fights?.map((item) => (
                        <MenuItem
                          key={item?.id}
                          value={item?.id}
                          onClick={() => setFightId(item?.id)}
                        >
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      Espaços
                    </Typography>
                    <Select
                      sx={{ marginTop: 1 }}
                      id="spaceId"
                      value={spaceId}
                      fullWidth
                      onChange={handleChange}
                    >
                      {dataOptions?.spaces?.map((item) => (
                        <MenuItem
                          key={item?.id}
                          value={item?.id}
                          onClick={() => setSpaceId(item?.id)}
                        >
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid2>
                  {/* <Grid2 size={12}>
                    <Stack
                      sx={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                        Limite de alunos?
                      </Typography>
                      <Switch
                        checked={studentLimit}
                        onClick={() => setStudentLimit(!studentLimit)}
                      />
                    </Stack>
                  </Grid2> */}
                  {studentLimit && (
                    <Grid2 size={12}>
                      <Input
                        id="limit"
                        name="limit"
                        label="Limite"
                        placeholder="Limite de alunos"
                        type="text"
                        fullWidth
                        size="small"
                        value={values.limit}
                        onChange={handleChange}
                      />
                    </Grid2>
                  )}

                  <Grid2 size={12}>
                    <Divider />
                    <Stack
                      sx={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                        Trava de check-in?
                      </Typography>
                      <Switch
                        checked={checkinLock}
                        onClick={() => setCheckinLock(!checkinLock)}
                      />
                    </Stack>
                    <Typography
                      sx={{ fontSize: 12, color: "secondary.contrastText" }}
                    >
                      Se ativada, os alunos não conseguem fazer check-in depois
                      de 10 minutos da aula iniciada.
                    </Typography>
                  </Grid2>
                </Grid2>
              </Grid2>
            </AccordionDetails>
          </Accordion>
        </Grid2>
        <Grid2 size={12}>
          <Accordion
            expanded={expanded === "panel2"}
            onChange={handleChangeAccordion("panel2")}
          >
            <AccordionSummary
              expandIcon={<IconArrowDown />}
              aria-controls="panel3-content"
              id="panel3-header"
            >
              <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
                <IconCalendar size={20} />
                <Typography>Calendário</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid2 size={12}>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <Stack>
                      <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                        Dias de aula
                      </Typography>
                      <ToggleButtonGroup
                        sx={{
                          marginTop: 1,
                          "& .MuiToggleButton-root": {
                            padding: isMobile ? "4px 6px" : "6px 12px",
                            fontSize: isMobile ? "10px" : "12px",
                          },
                        }}
                        id="dayWeek"
                        value={dayWeek}
                        onChange={handleDayWeek}
                      >
                        {daysOfWeek?.map((item) => (
                          <ToggleButton
                            key={item?.id}
                            value={item?.id}
                            sx={{
                              width: "100%",
                            }}
                            selected={dayWeek?.includes(item?.id)}
                          >
                            <Typography sx={{ fontSize: 12 }}>
                              {item?.name}
                            </Typography>
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Stack>
                  </Grid2>
                  <Grid2 size={6}>
                    <Input
                      id="startTime"
                      name="startTime"
                      label="Horário início"
                      type="time"
                      fullWidth
                      size="small"
                      value={values.startTime}
                      onChange={handleChange}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        e.target.showPicker()
                      }
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <Input
                      id="endTime"
                      name="endTime"
                      label="Horário fim"
                      type="time"
                      fullWidth
                      size="small"
                      value={values.endTime}
                      onChange={handleChange}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        e.target.showPicker()
                      }
                    />
                  </Grid2>
                </Grid2>
              </Grid2>
            </AccordionDetails>
          </Accordion>
        </Grid2>

        <Grid2 size={12}>
          <Accordion
            expanded={expanded === "panel3"}
            onChange={handleChangeAccordion("panel3")}
          >
            <AccordionSummary
              expandIcon={<IconArrowDown />}
              aria-controls="panel2-content"
              id="panel2-header"
            >
              <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
                <IconUsers size={20} />
                <Typography>Professores</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid2 size={12}>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      Selecione o professor
                    </Typography>
                  </Grid2>
                  {dataOptions?.professor?.length > 0 && (
                    <Grid2
                      width="100%"
                      container
                      spacing={2}
                      borderRadius={2}
                      bgcolor="secondary.main"
                    >
                      {dataOptions?.professor?.map((item) => (
                        <Grid2 key={item?.id} size={12}>
                          <Stack
                            sx={{
                              width: "100%",
                              flexDirection: "row",
                              backgroundColor: "secondary.dark",
                              alignItems: "center",
                              gap: 1,
                              padding: 1,
                              borderRadius: 2,
                            }}
                          >
                            <Checkbox
                              value={item?.id}
                              checked={professorId.includes(item?.id)}
                              onClick={() => {
                                setProfessorId(
                                  (prev) =>
                                    prev.includes(item?.id)
                                      ? prev.filter((id) => id !== item?.id) // remove
                                      : [...prev, item?.id] // adiciona
                                );
                              }}
                            />

                            <Typography
                              variant="body2"
                              fontWeight={600}
                              fontSize={16}
                            >
                              {item?.fullName}
                            </Typography>
                          </Stack>
                        </Grid2>
                      ))}
                    </Grid2>
                  )}
                  {dataOptions?.professor?.length === 0 && (
                    <Grid2 size={12}>
                      <Typography
                        variant="body1"
                        fontSize={16}
                        fontWeight={600}
                        sx={{
                          padding: 1,
                          borderRadius: 2,
                          backgroundColor: "secondary.dark",
                          textAlign: "center",
                        }}
                      >
                        Nenhum professor cadastrado
                      </Typography>
                    </Grid2>
                  )}
                </Grid2>
              </Grid2>
            </AccordionDetails>
          </Accordion>
        </Grid2>
        <Grid2 size={12}>
          <Accordion
            expanded={expanded === "panel4"}
            onChange={handleChangeAccordion("panel4")}
          >
            <AccordionSummary
              expandIcon={<IconArrowDown />}
              aria-controls="panel2-content"
              id="panel2-header"
            >
              <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
                <IconUsers size={20} />
                <Typography>Alunos</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid2 size={12}>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <Stack
                      width={"100%"}
                      direction={{ sx: "column", md: "row" }}
                      justifyContent={"space-between"}
                      alignItems={{ xs: "flex-start", md: "center" }}
                      gap={isMobile ? "10px" : "0px"}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                        Ao restringir a turma por alunos, apenas alunos
                        selecionados podem fazer check-in.
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<IconPlus size={18} />}
                        onClick={() => {
                          setOpenStudents(true);
                        }}
                      >
                        Adcionar
                      </Button>
                    </Stack>
                  </Grid2>
                  {dataOptions?.students?.length > 0 &&
                    dataOptions?.students
                      ?.filter((s) => studentsIds.includes(s?.id))
                      .map((item) => (
                        <Grid2 key={item?.id} size={12}>
                          <Stack
                            sx={{
                              width: "100%",
                              flexDirection: "row",
                              backgroundColor: "secondary.dark",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              padding: 1,
                              borderRadius: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              fontSize={16}
                            >
                              {item?.fullName}
                            </Typography>

                            <IconButton
                              onClick={() => {
                                setStudentsIds((prev) =>
                                  prev.filter((id) => id !== item?.id)
                                );
                              }}
                            >
                              <IconTrash color="red" />
                            </IconButton>
                          </Stack>
                        </Grid2>
                      ))}
                  {dataOptions?.students?.filter((s) =>
                    studentsIds.includes(s?.id)
                  )?.length === 0 && (
                    <Grid2 size={12}>
                      <Empty title={" "} />
                    </Grid2>
                  )}
                </Grid2>
              </Grid2>
            </AccordionDetails>
          </Accordion>
        </Grid2>

        <Grid2 size={12} mb={4}>
          <Stack
            width={"100%"}
            flexDirection={"row"}
            justifyContent={"flex-end"}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                if (!values.name) {
                  toast.error("Digite o nome da aula");
                  return;
                }
                if (!fightId) {
                  toast.error("Digite a modalidade");
                  return;
                }

                if (!spaceId) {
                  toast.error("Digite o espaço");
                  return;
                }

                if (dayWeek.length === 0) {
                  toast.error("Selecione pelo menos um dia da semana");
                  return;
                }

                handleSubmit();
              }}
            >
              Salvar
            </Button>
          </Stack>
        </Grid2>
      </Grid2>
      <Dialog open={openStudents} onClose={onCloseStudent} fullWidth>
        <DialogTitle>Lista de alunos</DialogTitle>
        <DialogContent>
          <Grid2 container gap={1}>
            <Grid2 size={12}>
              <Input
                value={searchStudent}
                onChange={(e) => {
                  setSearchStudent(e.target.value);
                }}
              />
            </Grid2>
            {!isEmpty(
              dataOptions?.students?.filter((e) =>
                e.fullName.toLowerCase().includes(searchStudent?.toLowerCase())
              )
            ) &&
              dataOptions.students
                ?.filter((e) =>
                  e.fullName
                    .toLowerCase()
                    .includes(searchStudent?.toLowerCase())
                )
                .map((item) => (
                  <Grid2 key={item?.id} size={12}>
                    <Stack
                      sx={{
                        width: "100%",
                        flexDirection: "row",
                        backgroundColor: "secondary.dark",
                        alignItems: "center",
                        gap: 1,
                        padding: 0.5,
                        borderRadius: 2,
                      }}
                    >
                      <Checkbox
                        value={item?.id}
                        checked={studentsIds.includes(item?.id)}
                        onClick={() => {
                          setStudentsIds((prev) =>
                            prev.includes(item?.id)
                              ? prev.filter((id) => id !== item?.id)
                              : [...prev, item?.id]
                          );
                        }}
                      />

                      <Typography
                        variant="body2"
                        fontWeight={600}
                        fontSize={16}
                      >
                        {item?.fullName}
                      </Typography>
                    </Stack>
                  </Grid2>
                ))}

            <Empty
              data={dataOptions?.students?.filter((e) =>
                e.fullName.toLowerCase().includes(searchStudent?.toLowerCase())
              )}
              title=" "
            />
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Stack p={1}>
            <Button onClick={onCloseStudent} variant="contained">
              Fechar
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </ContainerLoading>
  );
};

export default ClassDetails;
